(() => {
  const recommendationCache = (window.SoulCartRecommendationCache ||= new Map());
  const cartUiState = (window.SoulCartUiState ||= { shippingProgress: 0 });

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#039;',
      '"': '&quot;',
    })[character]);

  const currencyCode = () => window.Shopify?.currency?.active || 'USD';

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    const currency = currencyCode();

    try {
      const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      return `${formatted} ${currency}`;
    } catch (_error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  const imageUrl = (url, width = 180) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  class SoulShippingProgress extends HTMLElement {
    connectedCallback() {
      const target = Math.max(0, Math.min(100, Number(this.dataset.progress || 0)));
      const previous = Math.max(0, Math.min(100, Number(cartUiState.shippingProgress || 0)));

      this.style.setProperty('--soul-progress', `${previous}%`);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.style.setProperty('--soul-progress', `${target}%`);
        });
      });

      cartUiState.shippingProgress = target;
    }
  }

  if (!customElements.get('soul-shipping-progress')) {
    customElements.define('soul-shipping-progress', SoulShippingProgress);
  }

  class SoulCartUpsells extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.prevButton = null;
      this.nextButton = null;
      this.status = null;
      this.products = [];
      this.onScroll = this.updateControls.bind(this);
      this.onResize = this.updateControls.bind(this);
      this.onClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.soul-cart-upsells__track');
      this.prevButton = this.querySelector('.soul-cart-upsells__arrow--prev');
      this.nextButton = this.querySelector('.soul-cart-upsells__arrow--next');
      this.status = this.querySelector('.soul-cart-upsells__status');

      if (!this.track || !this.prevButton || !this.nextButton) return;

      this.addEventListener('click', this.onClick);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.loadRecommendations();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    get productIds() {
      return [...new Set(
        String(this.dataset.productIds || '')
          .split(',')
          .map((id) => Number(id.trim()))
          .filter(Boolean)
      )];
    }

    get limit() {
      return Math.max(1, Number(this.dataset.limit || 4));
    }

    async loadRecommendations() {
      const sourceIds = this.productIds;
      if (!sourceIds.length) {
        this.hide();
        return;
      }

      const groups = await Promise.allSettled(sourceIds.map((productId) => this.fetchRecommendations(productId)));
      const currentProductIds = new Set(sourceIds);
      const uniqueProducts = new Map();

      groups.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        result.value.forEach((product) => {
          const productId = Number(product?.id);
          if (!productId || currentProductIds.has(productId) || uniqueProducts.has(productId) || product.available === false) return;
          uniqueProducts.set(productId, product);
        });
      });

      this.products = [...uniqueProducts.values()].slice(0, this.limit);

      if (!this.products.length) {
        this.hide();
        return;
      }

      this.render();
    }

    fetchRecommendations(productId) {
      const endpoint = this.dataset.recommendationsUrl || '/recommendations/products.json';
      const cacheKey = `${endpoint}:${productId}:complementary`;
      if (recommendationCache.has(cacheKey)) return recommendationCache.get(cacheKey);

      const separator = endpoint.includes('?') ? '&' : '?';
      const request = fetch(`${endpoint}${separator}product_id=${productId}&limit=10&intent=complementary`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
          return response.json();
        })
        .then((data) => (Array.isArray(data?.products) ? data.products : []))
        .catch(() => []);

      recommendationCache.set(cacheKey, request);
      return request;
    }

    render() {
      this.track.innerHTML = this.products.map((product) => this.cardMarkup(product)).join('');
      requestAnimationFrame(() => this.updateControls());
    }

    cardMarkup(product) {
      const title = escapeHtml(product.title || '');
      const url = escapeHtml(product.url || `/products/${product.handle || ''}`);
      const image = imageUrl(product.featured_image, 180);
      const imageMarkup = image
        ? `<img class="soul-cart-upsell-card__image" src="${escapeHtml(image)}" alt="${title}" width="136" height="136" loading="lazy">`
        : `<span class="soul-cart-upsell-card__image soul-cart-upsell-card__image--placeholder"></span>`;

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const availableVariants = variants.filter((variant) => variant.available !== false);
      const isSimpleProduct = variants.length === 1 && availableVariants.length === 1;
      const price = product.price_varies ? `From ${formatMoney(product.price)}` : formatMoney(product.price);

      const action = isSimpleProduct
        ? `<button type="button" class="soul-cart-upsell-card__action" data-soul-upsell-add data-variant-id="${availableVariants[0].id}" data-product-id="${product.id}" aria-label="Add ${title} to cart"><span>+ ADD</span></button>`
        : `<a class="soul-cart-upsell-card__action" href="${url}" aria-label="Choose options for ${title}">SELECT</a>`;

      return `
        <article class="soul-cart-upsell-card" data-product-id="${product.id}">
          <a class="soul-cart-upsell-card__image-link" href="${url}" tabindex="-1" aria-hidden="true">${imageMarkup}</a>
          <div class="soul-cart-upsell-card__content">
            <a class="soul-cart-upsell-card__title" href="${url}">${title}</a>
            <p class="soul-cart-upsell-card__price">${escapeHtml(price)}</p>
          </div>
          ${action}
        </article>`;
    }

    handleClick(event) {
      const previous = event.target.closest('.soul-cart-upsells__arrow--prev');
      const next = event.target.closest('.soul-cart-upsells__arrow--next');
      const addButton = event.target.closest('[data-soul-upsell-add]');

      if (previous) {
        this.scrollBySlide(-1);
        return;
      }

      if (next) {
        this.scrollBySlide(1);
        return;
      }

      if (addButton) this.addProduct(addButton);
    }

    scrollBySlide(direction) {
      const card = this.track.querySelector('.soul-cart-upsell-card');
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0) || 0;
      const distance = (card?.getBoundingClientRect().width || this.track.clientWidth) + gap;
      this.track.scrollBy({ left: direction * distance, behavior: 'smooth' });
    }

    updateControls() {
      if (!this.track || !this.prevButton || !this.nextButton) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const hasOverflow = maxScroll > 2;
      this.prevButton.disabled = !hasOverflow || this.track.scrollLeft <= 2;
      this.nextButton.disabled = !hasOverflow || this.track.scrollLeft >= maxScroll - 2;
    }

    async addProduct(button) {
      if (button.disabled) return;

      const variantId = button.dataset.variantId;
      const cartDrawer = document.querySelector('cart-drawer');
      if (!variantId || !cartDrawer || !window.routes?.cart_add_url) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'ADDING…';

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);

        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add product');
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          try {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'soul-cart-upsells',
              productVariantId: variantId,
              cartData: data,
            });
          } catch (_error) {
            // Cart rendering below remains the source of truth.
          }
        }

        cartDrawer.setActiveElement?.(button);
        cartDrawer.renderContents(data);
      } catch (error) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
        if (this.status) this.status.textContent = 'Unable to add this item. Please try again.';
        console.error('[Soul Cart Upsells]', error);
      }
    }

    hide() {
      this.hidden = true;
    }
  }

  if (!customElements.get('soul-cart-upsells')) {
    customElements.define('soul-cart-upsells', SoulCartUpsells);
  }
})();
